import { useState, useEffect, useRef } from "react";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — Corporate / B2B Landing Page
   Route: /corporate
   
   One product. One pitch. Crystal only.
   "Every other gifting company picks a gift off a shelf.
    We build one from who the person actually is."
   ═══════════════════════════════════════════════════════════ */

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Outfit", Helvetica, sans-serif';

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

/* ── Gallery pieces (real sculptures from the platform) ── */
const GALLERY = [
  {
    name: "The Moonbeam",
    recipient: "Shashikala",
    description: "A sapphire elephant with book-page ears, rose-petal skin, and crescent-moon tusks",
    image: "/gallery/elephant.png",
    material: "Deep sapphire blue crystal with rose-gold veins",
    connections: [
      { element: "Book-page ears", from: "Education", match: "Elephant ears are fan-shaped. Open books are fan-shaped. Both receive and absorb knowledge.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Rose-petal skin", from: "Roses", match: "Rose petals overlap in layers. Elephant skin wrinkles overlap in layers. Both are armor made of something beautiful.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Crescent tusks", from: "Cancer zodiac / Moon", match: "Tusks curve in a crescent. The moon curves in a crescent. Both protect what they frame.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    name: "The Sacred Fan",
    recipient: "Priya",
    description: "An emerald peacock with temple gopuram tail feathers, a dancer's neck curve, and a spire crown",
    image: "/gallery/peacock.png",
    material: "Emerald green with gold temple-leaf gilding",
    connections: [
      { element: "Gopuram tail feathers", from: "Temple architecture", match: "Peacock feathers fan in radiating arcs. Temple gopurams rise in radiating tiers. Both are sacred geometry made visible.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Dancer's neck curve", from: "Bharatanatyam", match: "The peacock's neck curves in a perfect S. A dancer's arm in anjali mudra curves in a perfect S. Both are grace encoded in line.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Spire crown", from: "Temple architecture", match: "The peacock's crown rises in pointed plumes. A gopuram spire rises in pointed tiers. Both mark the highest point of something sacred.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    name: "Desert Tide",
    recipient: "Lena",
    description: "A sea turtle sculpted from desert sandstone with coral-reef shell patterns and ocean currents",
    image: "/gallery/turtle.png",
    material: "Desert sandstone with turquoise and copper patina",
    connections: [
      { element: "Coral-reef shell", from: "Marine biology", match: "A turtle's shell plates tessellate in hexagons. Coral polyps grow in hexagonal clusters. Both are nature's strongest architecture built by the smallest hands.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Desert-cracked skin", from: "Arizona childhood", match: "Turtle skin folds in cracked patterns. Desert earth cracks in the same geometry. Both are surfaces shaped by endurance.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Current-carved flippers", from: "Swimming", match: "Flippers slice through water in sweeping arcs. A swimmer's arms pull in the same arc. Both turn resistance into forward motion.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
];

/* ── Use cases ── */
const USE_CASES = [
  {
    icon: "◈",
    title: "Employee onboarding",
    body: "A new hire's first week sets the tone for years. Instead of a branded mug they'll forget, give them a crystal that captures who they are. It sits on their desk. It starts conversations. It says: we see you.",
  },
  {
    icon: "◇",
    title: "Work anniversaries & milestones",
    body: "Five years of someone's life deserves more than a gift card. A personalized sculpture turns a milestone into a permanent artifact — something that makes them feel like the company truly knows them.",
  },
  {
    icon: "✦",
    title: "Client appreciation",
    body: "After a $500K deal closes, the thank-you gift matters. A crystal designed from who your client actually is — their passions, their story — lands differently than anything off a catalog.",
  },
  {
    icon: "◎",
    title: "Leadership & executive gifts",
    body: "For board members, keynote speakers, and retiring executives. Museum-quality, deeply personal, impossible to re-gift. The kind of thing people post on LinkedIn.",
  },
];

/* ── Pricing tiers ── */
const TIERS = [
  {
    name: "Starter",
    quantity: "10 crystals",
    price: "$79",
    perUnit: "per piece",
    total: "$790",
    features: [
      "AI-generated personalized sculpture per recipient",
      "Crystal, figurine, or custom format",
      "Individual gift boxes",
      "Free US shipping",
      "Digital decode experience included",
    ],
    cta: "Get started",
  },
  {
    name: "Team",
    quantity: "25 pieces",
    price: "$129",
    perUnit: "per piece",
    total: "$3,225",
    featured: true,
    features: [
      "Everything in Starter",
      "Mix and match formats per recipient",
      "Dedicated onboarding call",
      "Custom branded gift card inserts",
      "Bulk CSV upload for recipient details",
    ],
    cta: "Book a demo",
  },
  {
    name: "Enterprise",
    quantity: "50+ pieces",
    price: "Custom",
    perUnit: "volume pricing",
    total: "Let's talk",
    features: [
      "Everything in Team",
      "Custom packaging & branding",
      "Large-scale or premium formats",
      "Quarterly gifting calendar",
      "Dedicated account manager",
    ],
    cta: "Contact us",
  },
];

export default function Corporate() {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({ name: "", email: "", company: "", size: "", message: "" });
  const [formStatus, setFormStatus] = useState("idle"); // idle | sending | sent | error
  const [decodedPieces, setDecodedPieces] = useState({}); // { 0: true, 1: false, ... }
  const formRef = useRef(null);
  const [visibleSections, setVisibleSections] = useState(new Set());

  /* ── Intersection observer for fade-in ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const sectionStyle = (id) => ({
    opacity: visibleSections.has(id) ? 1 : 0,
    transform: visibleSections.has(id) ? "translateY(0)" : "translateY(30px)",
    transition: "opacity 0.8s ease, transform 0.8s ease",
  });

  /* ── Form submit — sends straight to Gmail ── */
  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.company) return;
    setFormStatus("sending");
    try {
      const res = await fetch("https://formsubmit.co/ajax/krishnamud393@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          "order size": formData.size || "Not specified",
          message: formData.message || "No additional message",
          _subject: `Corporate Inquiry — ${formData.name} at ${formData.company}`,
          _template: "table",
        }),
      });
      if (res.ok) setFormStatus("sent");
      else setFormStatus("error");
    } catch {
      setFormStatus("error");
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0c",
      color: "#fff",
      fontFamily: SANS,
      overflowX: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes breathe {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        .corp-link {
          color: rgba(201,169,110,0.7);
          text-decoration: none;
          border-bottom: 1px solid rgba(201,169,110,0.2);
          transition: all 0.2s;
        }
        .corp-link:hover {
          color: rgba(201,169,110,1);
          border-color: rgba(201,169,110,0.5);
        }
      `}</style>

      {/* ═══ NAV ═══ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        padding: isMobile ? "14px 16px" : "14px 40px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "rgba(10,10,12,0.8)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}>
        <a href="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,169,110,0.6)", fontWeight: 500 }}>Yours Truly</span>
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 24 }}>
          <a href="/gallery" style={{ fontSize: 12, color: "rgba(232,230,226,0.4)", textDecoration: "none", fontWeight: 400 }}>Gallery</a>
          <button onClick={scrollToForm} style={{
            padding: "7px 18px", borderRadius: 20,
            background: "rgba(201,169,110,0.12)", border: "1px solid rgba(201,169,110,0.25)",
            color: "rgba(201,169,110,0.9)", fontSize: 12, fontWeight: 500,
            cursor: "pointer", fontFamily: SANS,
          }}>
            Book a demo
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: isMobile ? "100px 20px 60px" : "120px 40px 80px",
        textAlign: "center",
        position: "relative",
      }}>
        {/* Ambient glow */}
        <div style={{
          position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(201,169,110,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 800 }}>
          <div style={{
            fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase",
            color: "rgba(201,169,110,0.5)", marginBottom: 24, fontWeight: 500,
          }}>
            Corporate Gifting
          </div>

          <h1 style={{
            fontSize: isMobile ? 36 : 56,
            fontFamily: SERIF, fontWeight: 300, fontStyle: "italic",
            color: "rgba(232,230,226,0.95)",
            lineHeight: 1.15, marginBottom: 24,
          }}>
            Every other gifting company{" "}
            <span style={{ color: "rgba(232,230,226,0.35)" }}>picks a gift off a shelf.</span>
            <br />
            We build one from{" "}
            <span style={{
              background: "linear-gradient(90deg, rgba(201,169,110,0.9), rgba(220,200,150,0.9))",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>who the person actually is.</span>
          </h1>

          <p style={{
            fontSize: isMobile ? 15 : 17,
            color: "rgba(232,230,226,0.4)",
            lineHeight: 1.7, maxWidth: 560, margin: "0 auto 40px",
          }}>
            Personalized sculptures — delivered as laser-engraved crystals, 3D-printed figurines, or custom formats.
            Every piece is one-of-a-kind — designed from the recipient's story, passions, and identity.
            Not picked from a catalog. Created.
          </p>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={scrollToForm} style={{
              padding: "14px 36px", borderRadius: 28,
              background: "rgba(201,169,110,0.9)", border: "none",
              color: "#0a0a0c", fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: SANS,
              transition: "all 0.2s",
            }}>
              Book a demo
            </button>
            <a href="/gallery" style={{
              padding: "14px 36px", borderRadius: 28,
              background: "transparent", border: "1px solid rgba(232,230,226,0.12)",
              color: "rgba(232,230,226,0.6)", fontSize: 14, fontWeight: 500,
              cursor: "pointer", fontFamily: SANS, textDecoration: "none",
              display: "inline-flex", alignItems: "center",
              transition: "all 0.2s",
            }}>
              See the gallery
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{
          position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          animation: "breathe 3s ease-in-out infinite",
        }}>
          <div style={{ width: 1, height: 30, background: "linear-gradient(to bottom, transparent, rgba(201,169,110,0.3))" }} />
          <div style={{ fontSize: 10, color: "rgba(201,169,110,0.3)", letterSpacing: "0.15em" }}>SCROLL</div>
        </div>
      </section>

      {/* ═══ GALLERY ═══ */}
      <section
        id="gallery-section"
        data-animate
        style={{
          padding: isMobile ? "60px 20px 80px" : "60px 40px 100px",
          ...sectionStyle("gallery-section"),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>Real designs</div>
          <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)" }}>
            Every design tells a different story
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
          gap: 20,
          maxWidth: 1000, margin: "0 auto",
        }}>
          {GALLERY.map((piece, i) => {
            const isDecoded = decodedPieces[i];
            return (
            <div key={i} style={{
              background: "rgba(255,255,255,0.02)",
              border: isDecoded ? "1px solid rgba(201,169,110,0.2)" : "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
              overflow: "hidden",
              transition: "border-color 0.5s ease",
            }}>
              <div style={{
                height: isMobile ? 260 : 300,
                background: "rgba(0,0,0,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}>
                <img
                  src={piece.image}
                  alt={piece.name}
                  style={{
                    maxWidth: "90%", maxHeight: "90%",
                    objectFit: "contain",
                    filter: "drop-shadow(0 10px 30px rgba(200,170,100,0.15))",
                  }}
                />
              </div>
              <div style={{ padding: "20px 18px" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 6 }}>
                  Made for {piece.recipient}
                </div>
                <div style={{ fontSize: 18, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.85)", marginBottom: 8 }}>
                  {piece.name}
                </div>
                <p style={{ fontSize: 13, color: "rgba(232,230,226,0.35)", lineHeight: 1.6, marginBottom: 14 }}>
                  {piece.description}
                </p>

                {/* Decode button */}
                <button
                  onClick={() => setDecodedPieces(prev => ({ ...prev, [i]: !prev[i] }))}
                  style={{
                    width: "100%", padding: "10px 0", borderRadius: 10,
                    background: isDecoded ? "rgba(201,169,110,0.08)" : "rgba(255,255,255,0.03)",
                    border: isDecoded ? "1px solid rgba(201,169,110,0.2)" : "1px solid rgba(255,255,255,0.08)",
                    color: isDecoded ? "rgba(201,169,110,0.8)" : "rgba(232,230,226,0.4)",
                    fontSize: 12, fontWeight: 500, fontFamily: SANS,
                    cursor: "pointer", transition: "all 0.3s ease",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  <span style={{
                    display: "inline-block",
                    transform: isDecoded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
                    fontSize: 10,
                  }}>▼</span>
                  {isDecoded ? "Hide decode" : "Decode this design"}
                </button>

                {/* Decode reveal panel */}
                <div style={{
                  maxHeight: isDecoded ? 600 : 0,
                  opacity: isDecoded ? 1 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.6s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease",
                }}>
                  <div style={{ paddingTop: 16 }}>
                    {/* Material tag */}
                    <div style={{
                      fontSize: 11, color: "rgba(201,169,110,0.5)",
                      fontFamily: SERIF, fontStyle: "italic",
                      marginBottom: 14, paddingBottom: 12,
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                    }}>
                      {piece.material}
                    </div>

                    {/* Connection cards */}
                    {piece.connections.map((conn, j) => (
                      <div
                        key={j}
                        style={{
                          marginBottom: j < piece.connections.length - 1 ? 12 : 0,
                          padding: "12px 14px",
                          background: "rgba(201,169,110,0.03)",
                          border: "1px solid rgba(201,169,110,0.08)",
                          borderRadius: 10,
                          opacity: isDecoded ? 1 : 0,
                          transform: isDecoded ? "translateY(0)" : "translateY(12px)",
                          transition: `opacity 0.4s ease ${0.15 + j * 0.12}s, transform 0.4s ease ${0.15 + j * 0.12}s`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(232,230,226,0.8)" }}>
                            {conn.element}
                          </span>
                          <span style={{ fontSize: 10, color: "rgba(201,169,110,0.35)", fontFamily: SANS, whiteSpace: "nowrap", marginLeft: 8 }}>
                            ← {conn.from}
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: "rgba(232,230,226,0.35)", lineHeight: 1.55, marginBottom: 6 }}>
                          {conn.match}
                        </p>
                        <div style={{ fontSize: 9, letterSpacing: "0.1em", color: "rgba(201,169,110,0.3)" }}>
                          {conn.score}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* ═══ SOCIAL PROOF BAR ═══ */}
      <section style={{
        padding: isMobile ? "30px 20px" : "30px 40px",
        borderTop: "1px solid rgba(255,255,255,0.04)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        textAlign: "center",
      }}>
        <p style={{
          fontSize: 13, color: "rgba(232,230,226,0.25)", fontFamily: SERIF, fontStyle: "italic",
          maxWidth: 600, margin: "0 auto", lineHeight: 1.6,
        }}>
          "She saw the crystal and started crying. She said it was the most thoughtful gift she'd ever received."
          <span style={{ display: "block", marginTop: 8, fontSize: 11, color: "rgba(201,169,110,0.3)", fontStyle: "normal", fontFamily: SANS }}>
            — First customer reaction
          </span>
        </p>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section
        id="how-it-works"
        data-animate
        style={{
          padding: isMobile ? "80px 20px" : "100px 40px",
          maxWidth: 900, margin: "0 auto",
          ...sectionStyle("how-it-works"),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>How it works</div>
          <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)" }}>
            Three steps. Zero effort on your end.
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
          gap: isMobile ? 32 : 40,
        }}>
          {[
            {
              step: "Share",
              detail: "Send us a list of recipients. For each person, a few details — their role, passions, something that makes them them. A 2-minute form per person, or a bulk CSV.",
            },
            {
              step: "We sculpt",
              detail: "Our AI analyzes each person's story and creates a one-of-a-kind sculpture design — triple-matched to their identity. Then we bring it to life as a crystal, figurine, or custom format.",
            },
            {
              step: "They're speechless",
              detail: "Each recipient gets a sculpture that could only be theirs, plus a digital 'decode' experience revealing why every detail was chosen.",
            },
          ].map((item, i) => (
            <div key={i} style={{ textAlign: isMobile ? "left" : "center" }}>
              <div style={{
                fontSize: 48, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic",
                color: "rgba(201,169,110,0.15)", marginBottom: 16, lineHeight: 1,
              }}>
                {item.step}
              </div>
              <p style={{ fontSize: 14, color: "rgba(232,230,226,0.45)", lineHeight: 1.7 }}>
                {item.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FORMATS ═══ */}
      <section
        id="formats"
        data-animate
        style={{
          padding: isMobile ? "80px 20px" : "100px 40px",
          borderTop: "1px solid rgba(255,255,255,0.04)",
          ...sectionStyle("formats"),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>Choose the medium</div>
          <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)" }}>
            One design. Multiple ways to bring it to life.
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
          gap: 16,
          maxWidth: 900, margin: "0 auto",
        }}>
          {[
            {
              icon: "◆",
              name: "Crystal",
              desc: "3D laser-engraved inside a clear crystal block on an LED base. The light passes through the sculpture creating a holographic glow. Premium, elegant, made for the desk.",
              tag: "Most popular",
              price: "From $79",
            },
            {
              icon: "▲",
              name: "3D Figurine",
              desc: "Full-color 3D-printed sculpture you can hold in your hands. Museum-quality detail, exact to the AI-generated design. Bold, tactile, impossible to ignore.",
              tag: null,
              price: "From $149",
            },
            {
              icon: "✦",
              name: "Custom Format",
              desc: "Metal prints, acrylic displays, large-scale installations, branded packaging — if you can dream it, we'll figure out how to make it. Perfect for events and executive gifts.",
              tag: null,
              price: "Let's talk",
            },
          ].map((fmt, i) => (
            <div key={i} style={{
              padding: "28px 22px",
              background: i === 0 ? "rgba(201,169,110,0.04)" : "rgba(255,255,255,0.02)",
              border: i === 0 ? "1px solid rgba(201,169,110,0.2)" : "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
              position: "relative",
            }}>
              {fmt.tag && (
                <div style={{
                  position: "absolute", top: -9, left: 16,
                  fontSize: 10, fontWeight: 600, letterSpacing: "0.08em",
                  padding: "3px 12px", borderRadius: 12,
                  background: "rgba(201,169,110,0.9)", color: "#0a0a0c",
                }}>
                  {fmt.tag}
                </div>
              )}
              <div style={{ fontSize: 22, color: "rgba(201,169,110,0.4)", marginBottom: 14 }}>{fmt.icon}</div>
              <div style={{ fontSize: 17, fontWeight: 500, color: "rgba(232,230,226,0.85)", marginBottom: 8 }}>{fmt.name}</div>
              <p style={{ fontSize: 13, color: "rgba(232,230,226,0.4)", lineHeight: 1.7, marginBottom: 14 }}>{fmt.desc}</p>
              <div style={{ fontSize: 14, fontWeight: 500, color: "rgba(201,169,110,0.7)" }}>{fmt.price}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ USE CASES ═══ */}
      <section
        id="use-cases"
        data-animate
        style={{
          padding: isMobile ? "80px 20px" : "100px 40px",
          borderTop: "1px solid rgba(255,255,255,0.04)",
          ...sectionStyle("use-cases"),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>When to use it</div>
          <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)" }}>
            The moments that matter most
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 16,
          maxWidth: 900, margin: "0 auto",
        }}>
          {USE_CASES.map((uc, i) => (
            <div key={i} style={{
              padding: isMobile ? "24px 20px" : "28px 24px",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
            }}>
              <div style={{
                fontSize: 20, color: "rgba(201,169,110,0.4)", marginBottom: 12,
              }}>
                {uc.icon}
              </div>
              <div style={{
                fontSize: 16, fontWeight: 500, color: "rgba(232,230,226,0.85)", marginBottom: 10,
              }}>
                {uc.title}
              </div>
              <p style={{ fontSize: 13, color: "rgba(232,230,226,0.4)", lineHeight: 1.7 }}>
                {uc.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section
        id="pricing"
        data-animate
        style={{
          padding: isMobile ? "80px 20px" : "100px 40px",
          borderTop: "1px solid rgba(255,255,255,0.04)",
          ...sectionStyle("pricing"),
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>Pricing</div>
          <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)" }}>
            One product. Three scales.
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
          gap: 16,
          maxWidth: 960, margin: "0 auto",
        }}>
          {TIERS.map((tier, i) => (
            <div key={i} style={{
              padding: "32px 24px",
              background: tier.featured ? "rgba(201,169,110,0.04)" : "rgba(255,255,255,0.02)",
              border: tier.featured ? "2px solid rgba(201,169,110,0.3)" : "1px solid rgba(255,255,255,0.06)",
              borderRadius: 20,
              position: "relative",
              display: "flex", flexDirection: "column",
            }}>
              {tier.featured && (
                <div style={{
                  position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                  fontSize: 10, fontWeight: 600, letterSpacing: "0.08em",
                  padding: "3px 14px", borderRadius: 12,
                  background: "rgba(201,169,110,0.9)", color: "#0a0a0c",
                }}>
                  Most popular
                </div>
              )}
              <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(201,169,110,0.5)", marginBottom: 4 }}>
                {tier.name}
              </div>
              <div style={{ fontSize: 13, color: "rgba(232,230,226,0.5)", marginBottom: 16 }}>
                {tier.quantity}
              </div>
              <div style={{ marginBottom: 20 }}>
                <span style={{ fontSize: 36, fontWeight: 300, color: "rgba(201,169,110,0.9)", fontFamily: SANS }}>
                  {tier.price}
                </span>
                <span style={{ fontSize: 13, color: "rgba(232,230,226,0.3)", marginLeft: 6 }}>
                  {tier.perUnit}
                </span>
              </div>

              <div style={{ flex: 1, marginBottom: 24 }}>
                {tier.features.map((f, j) => (
                  <div key={j} style={{
                    fontSize: 13, color: "rgba(232,230,226,0.4)",
                    padding: "5px 0 5px 18px",
                    position: "relative", lineHeight: 1.5,
                  }}>
                    <span style={{ position: "absolute", left: 0, color: "rgba(120,200,140,0.5)", fontSize: 11 }}>✓</span>
                    {f}
                  </div>
                ))}
              </div>

              <button onClick={scrollToForm} style={{
                width: "100%", padding: "13px 0", borderRadius: 12,
                background: tier.featured ? "rgba(201,169,110,0.9)" : "rgba(255,255,255,0.06)",
                border: tier.featured ? "none" : "1px solid rgba(255,255,255,0.1)",
                color: tier.featured ? "#0a0a0c" : "rgba(232,230,226,0.7)",
                fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: SANS,
                transition: "all 0.2s",
              }}>
                {tier.cta}
              </button>
            </div>
          ))}
        </div>

        <p style={{
          textAlign: "center", marginTop: 20,
          fontSize: 12, color: "rgba(232,230,226,0.2)",
        }}>
          All tiers include free US shipping. International available on request. Pricing varies by format — crystals from $79, figurines from $149.
        </p>
      </section>

      {/* ═══ CONTACT / DEMO FORM ═══ */}
      <section
        ref={formRef}
        id="contact"
        data-animate
        style={{
          padding: isMobile ? "80px 20px 60px" : "100px 40px 80px",
          borderTop: "1px solid rgba(255,255,255,0.04)",
          ...sectionStyle("contact"),
        }}
      >
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>Let's talk</div>
            <h2 style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontWeight: 300, fontStyle: "italic", color: "rgba(232,230,226,0.9)", marginBottom: 12 }}>
              Book a demo
            </h2>
            <p style={{ fontSize: 14, color: "rgba(232,230,226,0.35)", lineHeight: 1.6 }}>
              We'll walk you through the experience, show you real examples, and design a sample crystal live on the call.
            </p>
          </div>

          {formStatus === "sent" ? (
            <div style={{
              textAlign: "center", padding: "40px 20px",
              background: "rgba(201,169,110,0.04)",
              border: "1px solid rgba(201,169,110,0.15)",
              borderRadius: 20,
            }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
              <div style={{ fontSize: 20, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.9)", marginBottom: 8 }}>
                We'll be in touch
              </div>
              <p style={{ fontSize: 13, color: "rgba(232,230,226,0.4)" }}>
                You'll hear from us within 24 hours.
              </p>
            </div>
          ) : (
            <div style={{
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 20, padding: isMobile ? "24px 20px" : "32px 28px",
            }}>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                  <input
                    type="text" placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={inputStyle}
                  />
                  <input
                    type="email" placeholder="Work email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                  <input
                    type="text" placeholder="Company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    style={inputStyle}
                  />
                  <select
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    style={{ ...inputStyle, appearance: "none", cursor: "pointer", color: formData.size ? "#fff" : "rgba(232,230,226,0.35)" }}
                  >
                    <option value="" style={{ background: "#0a0a0c" }}>Estimated order size</option>
                    <option value="10" style={{ background: "#0a0a0c" }}>~10 crystals</option>
                    <option value="25" style={{ background: "#0a0a0c" }}>~25 crystals</option>
                    <option value="50" style={{ background: "#0a0a0c" }}>50+ crystals</option>
                    <option value="100" style={{ background: "#0a0a0c" }}>100+ crystals</option>
                    <option value="unsure" style={{ background: "#0a0a0c" }}>Not sure yet</option>
                  </select>
                </div>
                <textarea
                  placeholder="Tell us about your gifting needs (optional)"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
                />
                <button
                  onClick={handleSubmit}
                  disabled={formStatus === "sending" || !formData.name || !formData.email || !formData.company}
                  style={{
                    width: "100%", padding: "14px 0", borderRadius: 12,
                    background: formData.name && formData.email && formData.company
                      ? "rgba(201,169,110,0.9)" : "rgba(201,169,110,0.15)",
                    border: "none",
                    color: formData.name && formData.email && formData.company
                      ? "#0a0a0c" : "rgba(10,10,12,0.4)",
                    fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: SANS,
                    transition: "all 0.2s",
                  }}
                >
                  {formStatus === "sending" ? "Sending..." : "Send inquiry"}
                </button>
                {formStatus === "error" && (
                  <p style={{ fontSize: 12, color: "rgba(255,120,100,0.7)", textAlign: "center" }}>
                    Something went wrong. Email us directly at hello@yourstruly.gift
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{
        padding: isMobile ? "30px 20px" : "30px 40px",
        borderTop: "1px solid rgba(255,255,255,0.04)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
      }}>
        <span style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.3)" }}>
          Yours Truly © {new Date().getFullYear()}
        </span>
        <div style={{ display: "flex", gap: 20 }}>
          <a href="/" style={{ fontSize: 12, color: "rgba(232,230,226,0.25)", textDecoration: "none" }}>Home</a>
          <a href="/gallery" style={{ fontSize: 12, color: "rgba(232,230,226,0.25)", textDecoration: "none" }}>Gallery</a>
          <a href="/create" style={{ fontSize: 12, color: "rgba(232,230,226,0.25)", textDecoration: "none" }}>Create</a>
        </div>
      </footer>
    </div>
  );
}

/* ── Shared input style ── */
const inputStyle = {
  padding: "12px 14px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#fff",
  fontSize: 14,
  fontFamily: '"Outfit", Helvetica, sans-serif',
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};
